import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, CreditCardIcon, Loader2Icon } from 'lucide-react';
import { PractitionerFlowShell } from '../components/practitioners/PractitionerFlowShell';
import { AppDownloadChooser } from '../components/practitioners/AppDownloadChooser';
import { ConfirmationStatusPanel } from '../components/practitioners/ConfirmationStatusPanel';
import { useMembershipConfirmation, type ConfirmationStatus } from '../hooks/useMembershipConfirmation';
import { welcomeNextSteps } from '../data/practitionerWelcome';
import { foundingPlans } from '../data/foundingOffer';
import { useScreenInit } from '../useScreenInit.js';
import { describeError, getPractitionerConnectStatus, getStoredPractitionerSession, redirectToPractitionerConnect, startPractitionerConnectOnboarding, type PractitionerConnectStatus } from '../utils/practitionerApi';

/**
 * /practitioners/welcome
 *
 * Membership billing and Stripe Connect are separate.
 *
 * Flow:
 *
 * 1. Stripe membership Checkout
 * 2. Backend confirms FemEntity membership
 * 3. Practitioner starts Stripe Connect onboarding
 * 4. Stripe hosts business/identity/bank setup
 * 5. Stripe returns to this page
 * 6. Backend retrieves the Connect account from Stripe
 * 7. Website displays the actual Stripe status
 *
 * Neither a membership redirect nor a Connect redirect
 * is treated as proof of successful setup by itself.
 */
export function PractitionerWelcomePage() {
  /**
   * Design-canvas preview only.
   *
   * Honoured when rendered inside the Magic Patterns
   * editor's Screens iframe with ?mp_screen=…
   */
  const screen = useScreenInit() as {
    confirmationPreview?: ConfirmationStatus;
  };
  const preview = window.self !== window.top ? screen.confirmationPreview : undefined;
  const {
    status,
    planId,
    trialEndDate,
    retry
  } = useMembershipConfirmation(preview);
  const [startingConnect, setStartingConnect] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [checkingConnectStatus, setCheckingConnectStatus] = useState(false);
  const [connectStatus, setConnectStatus] = useState<PractitionerConnectStatus | null>(null);
  const params = new URLSearchParams(window.location.search);

  /**
   * Stripe Connect returns here using:
   *
   * /practitioners/welcome?connect=return
   *
   * A return redirect does not prove that onboarding
   * was completed. We verify the connected account
   * through our backend.
   */
  const returnedFromConnect = params.get('connect') === 'return';

  /**
   * Stripe uses the refresh URL when an Account Link
   * expires or needs to be recreated.
   */
  const connectRefresh = params.get('connect') === 'refresh';

  /**
   * Check the real Stripe Connect status after Stripe
   * returns the practitioner to FemEntity.
   */
  useEffect(() => {
    if (!returnedFromConnect || status !== 'confirmed') {
      return;
    }
    let cancelled = false;
    async function checkConnectStatus() {
      setConnectError(null);
      setCheckingConnectStatus(true);
      const session = getStoredPractitionerSession();
      if (!session) {
        if (!cancelled) {
          setConnectError('Your secure session has expired. Please sign in again before checking your payment setup.');
          setCheckingConnectStatus(false);
        }
        return;
      }
      try {
        const result = await getPractitionerConnectStatus(session);
        if (!cancelled) {
          setConnectStatus(result.status);
        }
      } catch (error) {
        if (!cancelled) {
          setConnectError(describeError(error));
        }
      } finally {
        if (!cancelled) {
          setCheckingConnectStatus(false);
        }
      }
    }
    void checkConnectStatus();
    return () => {
      cancelled = true;
    };
  }, [returnedFromConnect, status]);

  /**
   * Start or resume Stripe-hosted Connect onboarding.
   *
   * create-practitioner-connect-account creates a new
   * Account Link each time, so this is also used when
   * Stripe says onboarding is incomplete.
   */
  async function handleSetUpPayments() {
    if (startingConnect) {
      return;
    }
    setConnectError(null);
    const session = getStoredPractitionerSession();
    if (!session) {
      setConnectError('Your secure session has expired. Please sign in again before setting up payments.');
      return;
    }
    try {
      setStartingConnect(true);
      const result = await startPractitionerConnectOnboarding(session);
      redirectToPractitionerConnect(result.onboardingUrl);
    } catch (error) {
      setConnectError(describeError(error));
      setStartingConnect(false);
    }
  }

  /**
   * Membership must be confirmed before we expose
   * practitioner payout setup.
   */
  if (status !== 'confirmed') {
    return <PractitionerFlowShell currentStep={1} data-id="element-835" data-mp="k9td74lz0bsf">
        <ConfirmationStatusPanel status={status} onRetry={retry} data-id="element-836" data-mp="kal9yirb0fka" />
      </PractitionerFlowShell>;
  }
  const planName = foundingPlans.find(plan => plan.id === planId)?.name;
  const complimentaryUntil = formatDate(trialEndDate);

  /**
   * App access should remain available after membership
   * confirmation. Connect setup is a separate payment
   * onboarding step.
   */
  return <PractitionerFlowShell currentStep={2} data-id="element-837" data-mp="z7c164qtqkyt">
      <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-838" data-mp="z6alfmyv5aar">
        {/* Membership confirmation */}
        <section className="rounded-[2rem] bg-purple-800 px-7 py-14 md:px-16 md:py-20 text-center" data-id="element-839" data-mp="1vq9uz4kzz4ig">
          <p className="font-serif italic text-lg text-purple-200" data-id="element-840" data-mp="1qxgq8gpj3w6d">
            {planName ? `${planName} · Founding Member` : 'Founding Member'}
          </p>

          <h1 className="mt-4 font-serif text-4xl md:text-6xl text-white leading-[1.05] tracking-tight" data-id="element-841" data-mp="3q5q9ihm537q6">
            Welcome, Founding Member 💗
          </h1>

          <p className="mt-6 font-serif text-xl md:text-2xl text-white" data-id="element-842" data-mp="3gfng4gb3vrrb">
            Your Fem Entity membership is confirmed.
          </p>

          <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg text-purple-100 font-light leading-relaxed" data-id="element-843" data-mp="32eui3llliejg">
            Welcome to Canada’s national women’s health
            network. Your first two months are complimentary,
            and you’re now ready to continue setting up your
            practitioner presence on Fem Entity.
          </p>

          <dl className="mt-12 mx-auto max-w-3xl grid gap-6 border-t border-white/15 pt-8 sm:grid-cols-3 text-center" data-id="element-844" data-mp="2xim6gi6lx3h1">
            <div data-id="element-845" data-mp="vpy2g6m52c07">
              <dt className="text-sm text-purple-200" data-id="element-846" data-mp="vod7ifvwwr1t">
                Membership
              </dt>

              <dd className="mt-1 font-serif text-xl text-white" data-id="element-847" data-mp="2g8icrq4f0f96">
                {planName ?? 'Confirmed'}
              </dd>
            </div>

            <div data-id="element-848" data-mp="2b6axaqkdmh23">
              <dt className="text-sm text-purple-200" data-id="element-849" data-mp="2pxa1zqi3clge">
                Months 1–2
              </dt>

              <dd className="mt-1 font-serif text-xl text-white" data-id="element-850" data-mp="2l6tn01ymgrrx">
                {complimentaryUntil ? `Free until ${complimentaryUntil}` : 'Complimentary'}
              </dd>
            </div>

            <div data-id="element-851" data-mp="2uvbj45vyiap8">
              <dt className="text-sm text-purple-200" data-id="element-852" data-mp="2uvtl7j5avj52">
                Founding rate
              </dt>

              <dd className="mt-1 font-serif text-xl text-white" data-id="element-853" data-mp="38vcuif9ul0iy">
                Locked in while active
              </dd>
            </div>
          </dl>
        </section>

        {returnedFromConnect ? <ConnectReturnSection checking={checkingConnectStatus} connectStatus={connectStatus} error={connectError} startingConnect={startingConnect} onContinue={handleSetUpPayments} data-id="element-854" data-mp="1eid5xwnhovzj" /> : <ConnectSetupSection refresh={connectRefresh} error={connectError} startingConnect={startingConnect} onContinue={handleSetUpPayments} data-id="element-855" data-mp="snw80ulp0hvs" />}

        {/* App next step */}
        <section aria-labelledby="next-heading" className="mt-20 md:mt-24 grid gap-12 lg:grid-cols-[minmax(0,1fr)_26rem] lg:gap-16 lg:items-start" data-id="element-856" data-mp="12gld97qngzio">
          <div data-id="element-857" data-mp="9qbswf8qcxod">
            <h2 id="next-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-858" data-mp="9rwn23r4uelp">
              Next: Download the Fem Entity app
            </h2>

            <p className="mt-5 max-w-xl text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-859" data-mp="1q4bo5cwmrz5a">
              Download the app and sign in using the same Fem
              Entity account you just created. From there,
              you’ll complete your practitioner profile, submit
              your credentials for verification, and get ready
              to connect with clients.
            </p>

            <h3 className="mt-12 text-sm font-semibold text-purple-700" data-id="element-860" data-mp="35vnn3uu0o6yg">
              In the app, you’ll
            </h3>

            <ol className="mt-6 border-l border-purple-200" data-id="element-861" data-mp="30xcf7pna86iv">
              {welcomeNextSteps.map((step, index) => <li key={step.title} className="relative pl-10 pb-8 last:pb-0" data-id="element-862" data-mp="2vz11v4xs3dvw" data-mpi={index}>
                    <span aria-hidden="true" className="absolute -left-[14px] top-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-purple-700 text-xs font-semibold text-white" data-id="element-863" data-mp="2mwjh6uxrutb0">
                      {index + 1}
                    </span>

                    <h4 className="font-serif text-xl text-charcoal leading-snug" data-id="element-864" data-mp="g5tq7ncrbidf">
                      {step.title}
                    </h4>

                    <p className="mt-1.5 text-base text-charcoal/70 leading-relaxed" data-id="element-865" data-mp="2c3ohrz3uia9o">
                      {step.description}
                    </p>
                  </li>)}
            </ol>
          </div>

          <div className="lg:sticky lg:top-32" data-id="element-866" data-mp="2h4vp5x22t7ft">
            <AppDownloadChooser data-id="element-867" data-mp="2h542tr60wgs2" />
          </div>
        </section>

        <p className="mt-20 text-center font-serif text-3xl md:text-4xl text-purple-800" data-id="element-868" data-mp="1te8ej40s03pk">
          See you inside. 💗
        </p>
      </div>
    </PractitionerFlowShell>;
}
interface ConnectReturnSectionProps {
  checking: boolean;
  connectStatus: PractitionerConnectStatus | null;
  error: string | null;
  startingConnect: boolean;
  onContinue: () => void;
}

/**
 * Display the Connect result only after the backend
 * has checked the actual Stripe connected account.
 */
function ConnectReturnSection({
  checking,
  connectStatus,
  error,
  startingConnect,
  onContinue
}: ConnectReturnSectionProps) {
  if (checking) {
    return <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-869" data-mp="vjfm2mh3w1ou">
        <div className="flex items-center gap-4" data-id="element-870" data-mp="lnkx79mahadm">
          <Loader2Icon aria-hidden="true" className="h-6 w-6 animate-spin text-purple-700" data-id="element-871" data-mp="low0l8btog47" />

          <div data-id="element-872" data-mp="qk2uxpj7c8gg">
            <p className="text-sm font-semibold text-purple-700" data-id="element-873" data-mp="1ec9pqk9a47dr">
              Payment setup
            </p>

            <h2 className="mt-1 font-serif text-3xl text-charcoal" data-id="element-874" data-mp="1jbmljui4oosh">
              Checking your Stripe account…
            </h2>

            <p className="mt-3 text-charcoal/70" data-id="element-875" data-mp="14d0y3y1vz4uk">
              We’re confirming your payment and payout
              status with Stripe.
            </p>
          </div>
        </div>
      </section>;
  }

  /**
   * Stripe reports that the account can accept charges
   * and receive payouts.
   */
  if (connectStatus === 'ready') {
    return <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-876" data-mp="20kfc8oyp89aa">
        <div className="flex items-start gap-4" data-id="element-877" data-mp="1vl2idk9eu1y7">
          <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-700 text-white" data-id="element-878" data-mp="1vnf7chdl5ckr">
            <CheckCircle2Icon aria-hidden="true" className="h-5 w-5" data-id="element-879" data-mp="1lvsopzyx4b7z" />
          </div>

          <div data-id="element-880" data-mp="1gxhfnrgg64qt">
            <p className="text-sm font-semibold text-purple-700" data-id="element-881" data-mp="1gx7r88b1w5n8">
              Payment setup complete
            </p>

            <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-882" data-mp="1ctjihmaon40e">
              Payments are set up
            </h2>

            <p className="mt-4 max-w-2xl text-base md:text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-883" data-mp="12x6laefxi80w">
              Stripe has confirmed your payment and payout
              setup. You can continue to the Fem Entity app
              and complete your practitioner profile.
            </p>
          </div>
        </div>
      </section>;
  }

  /**
   * Details were submitted, but Stripe has not yet
   * enabled both charges and payouts.
   */
  if (connectStatus === 'submitted') {
    return <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-884" data-mp="3ejqm09iqqeeo">
        <div className="max-w-3xl" data-id="element-885" data-mp="3jik289pewjij">
          <p className="text-sm font-semibold text-purple-700" data-id="element-886" data-mp="3jkxyglvtw1ro">
            Payment setup submitted
          </p>

          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-887" data-mp="2qu61hm6xv93a">
            Stripe is reviewing your setup
          </h2>

          <p className="mt-4 max-w-2xl text-base md:text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-888" data-mp="2vrxw4nyyx9b6">
            Stripe has received your information, but your
            payment account is not fully enabled yet. You can
            continue to the Fem Entity app while Stripe
            completes its review.
          </p>
        </div>
      </section>;
  }

  /**
   * Stripe account exists but onboarding was not
   * completed, or no Connect account has been started.
   */
  if (connectStatus === 'incomplete' || connectStatus === 'not_started') {
    return <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-889" data-mp="nmowvyt9mdc3">
        <div className="max-w-3xl" data-id="element-890" data-mp="iovsnbbivteg">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-700 text-white" data-id="element-891" data-mp="wm29h0cori11">
            <CreditCardIcon aria-hidden="true" className="h-5 w-5" data-id="element-892" data-mp="sls49pwpwvn8" />
          </div>

          <p className="mt-7 text-sm font-semibold text-purple-700" data-id="element-893" data-mp="16b2cqvy80zl1">
            Payment setup
          </p>

          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-894" data-mp="11dtz98o0pjq4">
            Finish setting up payments
          </h2>

          <p className="mt-5 max-w-2xl text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-895" data-mp="1bbqcc60imoqv">
            Stripe still needs some information before your
            payment account can be fully enabled.
          </p>

          {error && <ConnectError message={error} data-id="element-896" data-mp="1yxk44wfqjz0z" />}

          <ConnectButton loading={startingConnect} label="Continue with Stripe" onClick={onContinue} data-id="element-897" data-mp="1kzccn41gxpx7" />
        </div>
      </section>;
  }

  /**
   * The status request itself failed or the secure
   * browser session is no longer available.
   */
  return <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-898" data-mp="1g0j0bexx44ym">
      <div className="max-w-3xl" data-id="element-899" data-mp="129nq9zbonhtg">
        <p className="text-sm font-semibold text-purple-700" data-id="element-900" data-mp="126hyudfl03uk">
          Payment setup
        </p>

        <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-901" data-mp="175l5g2b7mi4n">
          We couldn’t confirm your Stripe status
        </h2>

        <p className="mt-4 max-w-2xl text-base md:text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-902" data-mp="x9rlhxv5vmcu">
          Your Fem Entity membership is confirmed, but we
          couldn’t verify your Stripe payment setup right now.
        </p>

        {error && <ConnectError message={error} data-id="element-903" data-mp="9cbs381gml44" />}
      </div>
    </section>;
}
interface ConnectSetupSectionProps {
  refresh: boolean;
  error: string | null;
  startingConnect: boolean;
  onContinue: () => void;
}
function ConnectSetupSection({
  refresh,
  error,
  startingConnect,
  onContinue
}: ConnectSetupSectionProps) {
  return <section className="mt-16 md:mt-20 rounded-[2rem] border border-purple-200 bg-purple-50 px-7 py-10 md:px-12 md:py-12" data-id="element-904" data-mp="s5pcwkbglb60">
      <div className="max-w-3xl" data-id="element-905" data-mp="n400p1ci2rgc">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-700 text-white" data-id="element-906" data-mp="i6i2a4w3zuml">
          <CreditCardIcon aria-hidden="true" className="h-5 w-5" data-id="element-907" data-mp="37vk18uwau9e6" />
        </div>

        <p className="mt-7 text-sm font-semibold text-purple-700" data-id="element-908" data-mp="32w7bo2e1mo7w">
          One more setup step
        </p>

        <h2 className="mt-2 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-909" data-mp="1ifg3plzckj14">
          Set up client payments
        </h2>

        <p className="mt-5 max-w-2xl text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-910" data-mp="1ne11zfv1gsmx">
          Connect your payout account securely through Stripe
          so you can receive payments from clients who book
          with you through Fem Entity.
        </p>

        <p className="mt-4 max-w-2xl text-sm text-charcoal/60 leading-relaxed" data-id="element-911" data-mp="221m5havtstvd">
          Stripe will securely collect the business,
          identity, and banking information required for
          payouts. Fem Entity does not collect your bank
          account information on this website.
        </p>

        {refresh && <div className="mt-6 rounded-xl border border-purple-200 bg-white px-5 py-4 text-sm text-charcoal/70" data-id="element-912" data-mp="2g1ng3i1w7fo1">
            Your previous Stripe setup session expired or
            needs to be reopened. Select the button below
            to continue.
          </div>}

        {error && <ConnectError message={error} data-id="element-913" data-mp="2nk4r5spf32j4" />}

        <ConnectButton loading={startingConnect} label={refresh ? 'Continue payment setup' : 'Set up payments with Stripe'} onClick={onContinue} data-id="element-914" data-mp="2inmsnakok0f8" />
      </div>
    </section>;
}
interface ConnectButtonProps {
  loading: boolean;
  label: string;
  onClick: () => void;
}
function ConnectButton({
  loading,
  label,
  onClick
}: ConnectButtonProps) {
  return <button type="button" onClick={onClick} disabled={loading} className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-purple-700 px-7 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-915" data-mp="1zq3i22ikq9qp">
      {loading ? <>
          <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-916" data-mp="3sk33jiym09j5" />

          Opening Stripe…
        </> : <>
          {label}

          <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-917" data-mp="3jl7ve1c08pd7" />
        </>}
    </button>;
}
function ConnectError({
  message
}: {
  message: string;
}) {
  return <div role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-id="element-918" data-mp="f79e8lg1nh7a">
      {message}
    </div>;
}
function formatDate(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-CA', {
    month: 'long',
    day: 'numeric'
  });
}